import EmailApi from "~/api/email";
import EmailAccountApi from "~/api/email_account";
import MailBoxApi from "~/api/mailbox";
import TemplateApi from "~/api/template";
import AccountStateEnum from "../enums/AccountStateEnum";
import MatchStateEnum from "../enums/MatchStateEnum";
import ViewStateEnum from "../enums/ViewStateEnum";

// Loading
const initialiseStore = async (context) => {
  await context.dispatch("loadAccounts");
  await context.dispatch("loadUnprocessedEmailCount");
  await context.dispatch("loadTemplates");
  await context.dispatch("loadEmails");
  context.commit("SET_LOADING", false);
};

const initialiseStoreForCustomer = async (context) => {
  context.commit("SET_SEND_STATE", null);
  await context.dispatch("loadAccounts");
  await context.dispatch("loadTemplates");
};

const initialiseStoreForCustomerByEmailId = async (context, emailId) => {
  await context.dispatch("loadAccounts");
  await context.dispatch("loadTemplates");
  await context.dispatch("loadEmailById", emailId);
};

// Load Accounts / Mailboxes
const loadAccounts = async (context) => {
  const userId = window.store.state.user.id;

  await Promise.all([
    MailBoxApi.getWithoutEmailAccountByUser(userId),
    EmailAccountApi.getByUser(userId),
  ])
    .then(([mailBoxes, emailAccounts]) => {
      const accounts = [
        ...mailBoxes.data.map((mailbox) => ({ ...mailbox, isMailbox: true })),
        ...emailAccounts.data,
      ];
      context.commit("SET_ACCOUNTS", accounts);
      if (accounts.length > 0) context.commit("SET_SELECTED_ACCOUNT", accounts[0]);
    });
};

// Load Unprocessed Email Count
const loadUnprocessedEmailCount = async (context) => {
  context.state.accounts.map(async (account) => {
    let count;
    if (account.isMailbox) {
      count = (await EmailApi.countUnprocessedEmailsByMailbox(account.id)).count;
    } else {
      count = (await EmailApi.countUnprocessedEmailsByEmailAccount(account.id)).count;
    }
    context.commit("SET_UNPROCESSED_EMAIL_COUNT", { id: account.id, count });
  });
};

// Load Templates
const loadTemplates = async (context) => {
  const { data: templates } = await TemplateApi.paginateEmailTemplates(0, 10000);
  context.commit("SET_TEMPLATES", templates);
};

// Load Emails
// main load function underneath getEmailAccountEmails and getEmailBoxEmails
const getEmailAccountEmails = async (context, selectedAccount) => {
  switch (context.state.accountState) {
    case AccountStateEnum.UNPROCESSED:

      // eslint-disable-next-line no-case-declarations
      const {
        data: unprocessedEmails,
        meta: { pagination: unprocessedPagination },
      } = await EmailApi.getPaginatedUnprocessedByEmailAccount(
        selectedAccount.id,
        context.state.pagination.page,
        context.state.pagination.perPage,
        context.state.pagination.orderBy,
        context.state.pagination.order,
        ["customer", "attachable.attachable_category.attachable"],
        context.state.filter,
      );
      return { emails: unprocessedEmails, pagination: unprocessedPagination };

    case AccountStateEnum.PROCESSED:

      // eslint-disable-next-line no-case-declarations
      const {
        data: processedEmails,
        meta: { pagination: processedPagination },
      } = await EmailApi.getPaginatedProcessedByEmailAccount(
        selectedAccount.id,
        context.state.pagination.page,
        context.state.pagination.perPage,
        context.state.pagination.orderBy,
        context.state.pagination.order,
        ["customer", "attachable.attachable_category.attachable"],
        context.state.filter,
      );
      return { emails: processedEmails, pagination: processedPagination };

    case AccountStateEnum.OUTBOX:

      // eslint-disable-next-line no-case-declarations
      const {
        data: outgoingEmails,
        meta: { pagination: outgoingPagination },
      } = await EmailApi.getPaginatedOutgoingByEmailAccount(
        selectedAccount.id,
        context.state.pagination.page,
        context.state.pagination.perPage,
        context.state.pagination.orderBy,
        context.state.pagination.order,
        ["customer", "attachable.attachable_category.attachable"],
        context.state.filter,
      );
      return { emails: outgoingEmails, pagination: outgoingPagination };

    case AccountStateEnum.CONCEPTS:
      // load concepts
      return { emails: [], pagination: {} };

    default:
      return { emails: [], pagination: {} };
  }
};

const getMailboxEmails = async (context, selectedAccount) => {
  switch (context.state.accountState) {
    case AccountStateEnum.UNPROCESSED:

      // eslint-disable-next-line no-case-declarations
      const {
        data: unprocessedEmails,
        meta: { pagination: unprocessedPagination },
      } = await EmailApi.getPaginatedUnprocessedByMailbox(
        selectedAccount.id,
        context.state.pagination.page,
        context.state.pagination.perPage,
        context.state.pagination.orderBy,
        context.state.pagination.order,
        ["customer", "attachable.attachable_category.attachable"],
        context.state.filter,
      );
      return { emails: unprocessedEmails, pagination: unprocessedPagination };

    case AccountStateEnum.PROCESSED:

      // eslint-disable-next-line no-case-declarations
      const {
        data: processedEmails,
        meta: { pagination: processedPagination },
      } = await EmailApi.getPaginatedProcessedByMailbox(
        selectedAccount.id,
        context.state.pagination.page,
        context.state.pagination.perPage,
        context.state.pagination.orderBy,
        context.state.pagination.order,
        ["customer", "attachable.attachable_category.attachable"],
        context.state.filter,
      );
      return { emails: processedEmails, pagination: processedPagination };

    case AccountStateEnum.CONCEPTS:
      // load concepts
      return { emails: [], pagination: {} };

    default:
      return { emails: [], pagination: {} };
  }
};

const validateEmail = (email) => {
  // eslint-disable-next-line no-param-reassign
  if (!email.subject) email.subject = "Geen onderwerp";
  // eslint-disable-next-line no-param-reassign
  email.attachments = email.data?.attachments?.map((attachment, index) => ({
    ...attachment,
    index,
    should_import: true,
  })) || [];
  return email;
};

const loadEmails = async (context) => {
  const { selectedAccount } = context.state;
  if (!selectedAccount) return false;

  const { emails, pagination } = (selectedAccount.isMailbox)
    ? await getMailboxEmails(context, selectedAccount)
    : await getEmailAccountEmails(context, selectedAccount);

  return context.commit("SET_EMAILS", {
    emails: emails.map((email) => validateEmail(email)),
    pagination,
  });
};

const loadEmailById = async (context, emailId) => {
  const { data: email } = await EmailApi.getById(emailId, ["customer", "attachable.attachable_category.attachable"]);

  context.commit("SET_EMAILS", {
    emails: [validateEmail(email)],
    pagination: {},
  });
  context.commit("SET_SELECTED_EMAILS", [email]);

  return context.commit("SET_LOADING", false);
};

// Filter
const setPagination = async (context, pagination) => {
  await context.commit("SET_PAGINATION", pagination);
  await context.dispatch("loadEmails");
};

const clearFilter = (context) => {
  context.commit("SET_FILTER", {
    dateRange: [],
    sender: "",
    subject: "",
  });
  context.dispatch("loadEmails");
};

// Select account
const setSelectedAccount = async (context, accountId) => {
  await context.commit("SET_LOADING", true);
  const account = context.state.accounts.find((acc) => acc.id === accountId);
  context.commit("SET_SELECTED_ACCOUNT", account);
  context.commit("SET_ACCOUNT_STATE", AccountStateEnum.UNPROCESSED);
  context.commit("SET_SELECTED_EMAILS", []);
  context.commit("SET_VIEW_STATE", ViewStateEnum.INBOX);
  await context.dispatch("loadEmails");
  context.commit("SET_LOADING", false);
};

// Select emails
const setSelectedEmails = async (context, emailIds) => {
  const emails = emailIds.map((id) => {
    const email = { ...context.state.emails.find((v) => v.id === id) };
    email.data = email.data || {};
    email.data.attachments = email.data.attachments?.map((attachment) => ({
      ...(attachment),
      should_import: true,
    })) || [];
    email.attachAttachments = false;
    return email;
  });
  context.commit("SET_SELECTED_EMAILS", emails);
};

// states
const setAccountState = async (context, value) => {
  await context.commit("SET_LOADING", true);
  await context.commit("SET_ACCOUNT_STATE", value);
  await context.commit("SET_EMAILS", []);
  await context.commit("SET_SELECTED_EMAILS", []);
  await context.commit("SET_PAGE", undefined);
  await context.commit("SET_VIEW_STATE", ViewStateEnum.INBOX);
  await context.dispatch("loadEmails");
  await context.commit("SET_LOADING", false);
};

const setSendState = async (context, value) => {
  context.commit("SET_SEND_STATE", value);
};

const setMatchState = async (context, value) => {
  context.commit("SET_MATCH_STATE", value);
};

// Switching views
const replaceHistoryState = (view) => {
  if (window.history.state?.view === ViewStateEnum.INBOX) window.history.pushState({ view }, "");
};
const goBack = () => {
  window.history.back();
};

const showInbox = async (context) => {
  context.commit("SET_VIEW_STATE", ViewStateEnum.INBOX);
  await context.dispatch("setSelectedEmails", []);
  context.commit("SET_MATCH_STATE", undefined);
  await context.commit("SET_CREATE_EMAIL_CUSTOMER_ID", undefined);
};

const showViewEmail = async (context, emailIds) => {
  if (emailIds) await context.dispatch("setSelectedEmails", emailIds);
  context.commit("SET_VIEW_STATE", ViewStateEnum.VIEW);
  replaceHistoryState(ViewStateEnum.VIEW);
};

const showReplyOrForwardEmail = async (context, emailIds) => {
  if (emailIds) await context.dispatch("setSelectedEmails", emailIds);
  context.commit("SET_VIEW_STATE", ViewStateEnum.REPLY_OR_FORWARD);
  replaceHistoryState(ViewStateEnum.REPLY_OR_FORWARD);
};

const showMatchEmail = async (context, emailIds) => {
  if (emailIds) await context.dispatch("setSelectedEmails", emailIds);
  if (emailIds?.length > 1) {
    context.commit("SET_VIEW_STATE", ViewStateEnum.MATCH);
    context.commit("SET_MATCH_STATE", MatchStateEnum.MULTIPLE);
  } else {
    context.commit("SET_MATCH_STATE", MatchStateEnum.SINGLE);
  }
  replaceHistoryState(ViewStateEnum.MATCH);
};

const showCreateEmail = async (context, emailId) => {
  await context.commit("SET_CREATE_EMAIL_CUSTOMER_ID", emailId);
  await context.commit("SET_VIEW_STATE", ViewStateEnum.CREATE);
  await context.commit("SET_SELECTED_EMAILS", []);
  replaceHistoryState(ViewStateEnum.CREATE);
};

const showSingleEmail = async (context, emailId) => {
  await context.dispatch("loadEmailById", emailId);
  context.commit("SET_VIEW_STATE", ViewStateEnum.SHOW_SINGLE);
  replaceHistoryState(ViewStateEnum.SHOW_SINGLE);
};

export default {
  initialiseStore,
  initialiseStoreForCustomer,
  initialiseStoreForCustomerByEmailId,
  loadAccounts,
  loadUnprocessedEmailCount,
  loadTemplates,
  loadEmails,
  loadEmailById,
  setPagination,
  clearFilter,
  setSelectedAccount,
  setSelectedEmails,
  setAccountState,
  setSendState,
  setMatchState,
  goBack,
  showInbox,
  showViewEmail,
  showReplyOrForwardEmail,
  showMatchEmail,
  showCreateEmail,
  showSingleEmail,
};
